/**
 * LabelFilterUI Component
 *
 * Unified filter UI component with three variants:
 * - inline: Horizontal layout for sidebars (chips + add button + clear)
 * - popover: Vertical layout for popovers (header + chips + add button)
 * - card: Simple layout for cards (label + chips row)
 */

import { Filter, X, Plus } from 'lucide-react'
import { Dropdown } from 'antd'
import { LabelChip } from '../../../components/common/LabelChip'
import type { LabelFilterUIProps } from '../types'
import {
  PopoverContainer,
  FilterHeader,
  FilterTitle,
  ChipsContainer,
  AddFilterButtonPrimary,
  AddFilterButtonSubtle,
  ClearButton,
  EmptyMessage,
  NoFilterText,
  DropdownLabelItem,
  ColorSwatch,
  InlineContainer,
  InlineSection,
  InlineLabel,
  CardLabelText,
} from './styles'

/**
 * Unified label filter UI component.
 *
 * @example
 * ```tsx
 * // Inline variant (for ReportSidebar)
 * <LabelFilterUI
 *   variant="inline"
 *   selectedLabels={selectedLabels}
 *   onLabelsChange={setSelectedLabels}
 *   availableLabels={projectLabels}
 * />
 *
 * // Popover variant (for MindMapHeader)
 * <Popover content={
 *   <LabelFilterUI
 *     variant="popover"
 *     selectedLabels={selectedLabels}
 *     onLabelsChange={setSelectedLabels}
 *     availableLabels={projectLabels}
 *   />
 * }>
 *   <Button>Filter</Button>
 * </Popover>
 *
 * // Card variant (for ReportPage)
 * <LabelFilterUI
 *   variant="card"
 *   selectedLabels={selectedLabels}
 *   onLabelsChange={setSelectedLabels}
 *   availableLabels={projectLabels}
 * />
 * ```
 */
export function LabelFilterUI({
  selectedLabels,
  onLabelsChange,
  availableLabels,
  variant,
  className,
  disabled = false,
}: LabelFilterUIProps) {
  const hasActiveFilter = selectedLabels.length > 0
  const allLabelsSelected = selectedLabels.length === availableLabels.length
  const noLabelsAvailable = availableLabels.length === 0

  const handleRemoveLabel = (labelId: string) => {
    onLabelsChange(selectedLabels.filter((id) => id !== labelId))
  }

  const handleAddLabel = (labelId: string) => {
    onLabelsChange([...selectedLabels, labelId])
  }

  const handleClear = () => {
    onLabelsChange([])
  }

  // Dropdown menu items for adding labels
  const dropdownItems = availableLabels
    .filter((label) => !selectedLabels.includes(label.id))
    .map((label) => ({
      key: label.id,
      label: (
        <DropdownLabelItem>
          <ColorSwatch $color={label.color} />
          <span>{label.label_text}</span>
        </DropdownLabelItem>
      ),
      onClick: () => handleAddLabel(label.id),
    }))

  // Render chips for selected labels
  const renderChips = () =>
    selectedLabels.map((labelId) => {
      const label = availableLabels.find((l) => l.id === labelId)
      if (!label) return null
      return (
        <LabelChip
          key={labelId}
          text={label.label_text}
          color={label.color}
          onRemove={disabled ? undefined : () => handleRemoveLabel(labelId)}
          alwaysShowRemove={!disabled}
        />
      )
    })

  // Render add button with dropdown
  const renderAddButton = (buttonVariant: 'primary' | 'subtle') => {
    const isDisabled = disabled || allLabelsSelected || noLabelsAvailable
    const ButtonComponent =
      buttonVariant === 'primary' ? AddFilterButtonPrimary : AddFilterButtonSubtle

    return (
      <Dropdown
        menu={{ items: dropdownItems }}
        disabled={isDisabled}
        trigger={['click']}
        getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
      >
        <ButtonComponent $disabled={isDisabled} disabled={isDisabled} aria-label="Add filter label">
          <Plus size={buttonVariant === 'primary' ? 14 : 12} color="currentColor" />
        </ButtonComponent>
      </Dropdown>
    )
  }

  // Inline variant - horizontal layout for sidebars
  if (variant === 'inline') {
    return (
      <InlineContainer className={className}>
        <InlineSection>
          <InlineLabel>
            <Filter size={14} />
            Filter
          </InlineLabel>
          <ChipsContainer>
            {!hasActiveFilter && <NoFilterText>All items</NoFilterText>}
            {renderChips()}
            {renderAddButton('primary')}
          </ChipsContainer>
        </InlineSection>
        {hasActiveFilter && !disabled && (
          <ClearButton onClick={handleClear}>
            <X size={12} />
            Clear
          </ClearButton>
        )}
      </InlineContainer>
    )
  }

  // Popover variant - vertical layout for header popover
  if (variant === 'popover') {
    return (
      <PopoverContainer className={className}>
        <FilterHeader>
          <FilterTitle>
            <Filter size={14} />
            Filter by Labels
          </FilterTitle>
          {hasActiveFilter && !disabled && (
            <ClearButton onClick={handleClear}>
              <X size={12} />
              Clear
            </ClearButton>
          )}
        </FilterHeader>

        {noLabelsAvailable ? (
          <EmptyMessage>No labels available. Create labels on nodes first.</EmptyMessage>
        ) : (
          <ChipsContainer>
            {renderChips()}
            {renderAddButton('subtle')}
          </ChipsContainer>
        )}
      </PopoverContainer>
    )
  }

  // Card variant - simple layout for report page
  return (
    <div className={className}>
      <CardLabelText>Filter by labels:</CardLabelText>
      {noLabelsAvailable ? (
        <EmptyMessage>No labels available.</EmptyMessage>
      ) : (
        <ChipsContainer>
          {renderChips()}
          {renderAddButton('subtle')}
        </ChipsContainer>
      )}
    </div>
  )
}
